import React, { useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

const scoreExplanations = [
  {
    name: 'Distance Score (70%)',
    description: 'How closely the total trip distance matches the requested rental kilometers.',
    formula: '1.0 - min(|totalDistance - requestedDistance| / requestedDistance, 1.0)',
    impact: 'Ensures the generated route has approximately the same distance as the original rental.'
  },
  {
    name: 'Distribution Score (10%)',
    description: 'How evenly the stops are distributed along the route.',
    formula: '1.0 - min(std(distances) / mean(distances), 1.0)',
    impact: 'Prevents clustering of stops in one area and large gaps in others.'
  },
  {
    name: 'Route Efficiency Score (20%)',
    description: 'Penalizes illogical routes with backtracking.',
    formula: '1.0 - avg(penaltiesForAnglesOver120°)',
    impact: 'Ensures a logical route progression without visiting the same area twice.'
  }
];


const WorkflowExplanation = () => {
  const [expanded, setExpanded] = useState(false);

  const handleChange = () => {
    setExpanded(!expanded);
  };

  return (
    <Paper elevation={2} sx={{ mt: 4, p: 0, borderRadius: '8px', overflow: 'hidden' }}>
      <Accordion expanded={expanded} onChange={handleChange}>
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            bgcolor: 'primary.light', 
            color: 'white',
            '&:hover': {
              bgcolor: 'primary.main',
            }
          }}
        >
          <Typography variant="h6">
            How the Trip Generator Works
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Box sx={{ p: 3 }}>
            <Typography variant="body1" paragraph sx={{ fontWeight: 'medium' }}>
              This advanced tool creates extremely accurate trips for your rental data using sophisticated algorithms and a simple interface.
            </Typography>
            
            <Box sx={{ ml: 2 }}>
              <Typography variant="body1" component="div">
                <ul>
                  <li><strong>High-Precision Distance Matching:</strong> Creates routes with 99% accuracy to your required kilometers using advanced optimization</li>
                  <li><strong>Massive Search Capability:</strong> Analyzes hundreds of thousands of potential route combinations using the distance cache</li>
                  <li><strong>Multi-Phase Optimization:</strong> Combines random sampling with iterative refinement techniques to find the optimal solution</li>
                  <li><strong>Population-Based Algorithm:</strong> Maintains and evolves a population of high-quality routes to continuously improve results</li>
                  <li><strong>Simulated Annealing Optimization:</strong> Fine-tunes stop order using advanced route optimization techniques</li>
                  <li><strong>Intelligent Stop Selection:</strong> Selects from a wide range of potential stops while maintaining a logical route</li>
                  <li><strong>Profile-Specific Caching:</strong> Leverages separate distance caches and stop databases for different customer profiles</li>
                </ul>
              </Typography>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography variant="subtitle1" sx={{ fontWeight: 'medium', mb: 1 }}>
              How It Works
            </Typography>
            
            <Typography variant="body2" paragraph>
              1. <strong>Initial Analysis:</strong> Calculates direct distance between start and end points to determine additional distance needed
            </Typography>
            
            <Typography variant="body2" paragraph>
              2. <strong>Candidate Selection:</strong> Filters potential stops within an 800km radius of start/end points
            </Typography>
            
            <Typography variant="body2" paragraph>
              3. <strong>Random Sampling:</strong> Generates up to 10000 random combinations for each stop count (default 15-40 stops)
            </Typography>
            
            <Typography variant="body2" paragraph>
              4. <strong>Population Refinement:</strong> Maintains a population of top 50 solutions and iteratively improves them through multiple generations
            </Typography>
            
            <Typography variant="body2" paragraph>
              5. <strong>Route Optimization:</strong> Applies greedy algorithms and simulated annealing to optimize stop order for logical travel
            </Typography>
            
            <Typography variant="body2" paragraph>
              6. <strong>Solution Filtering:</strong> Returns routes with less than 2% error from target distance, with preference for solutions under 1%
            </Typography>
            
            <Box sx={{ mt: 2, bgcolor: '#f5f5f5', p: 2, borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                The algorithm can analyze hundreds of thousands of potential route combinations in seconds using the cached distance database, 
                selecting the optimal solution that precisely matches your required distance while maintaining realistic travel patterns.
              </Typography>
            </Box>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default WorkflowExplanation;